import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { getConfig } from '../lib/config';

// Cloudflare Turnstile (https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/).
// Explicit rendering, managed mode; the server verifies the token with Siteverify.
interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  reset(id?: string): void;
  remove(id: string): void;
}
declare global { interface Window { turnstile?: TurnstileApi } }

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let loading: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!loading) {
    loading = new Promise<TurnstileApi>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT;
      s.async = true;
      s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile unavailable')));
      s.onerror = () => { s.remove(); reject(new Error('Turnstile script blocked')); };
      document.head.append(s);
    }).catch((e) => { loading = null; throw e; });
  }
  return loading;
}

const siteTheme = () => (document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark');

export interface HumanCheck {
  /** null while the server config loads; false when the server doesn't require verification. */
  required: boolean | null;
  token: string | null;
  /** The widget, or null when not required. Render it inside the form. */
  box: JSX.Element | null;
  /** Tokens are single-use: call after every submit that reached the server. */
  reset: () => void;
  /** Message to show when the user submits before a token exists. */
  pendingMessage: string;
}

export function useHumanCheck(action: string): HumanCheck {
  const [siteKey, setSiteKey] = useState<string | null | undefined>(undefined);
  const [token, setToken] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [theme, setTheme] = useState(siteTheme);
  const el = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);

  useEffect(() => { let live = true; getConfig().then((c) => live && setSiteKey(c?.turnstileSiteKey ?? null)); return () => { live = false; }; }, []);
  useEffect(() => {
    const on = () => setTheme(siteTheme());
    window.addEventListener('ayx:theme', on);
    return () => window.removeEventListener('ayx:theme', on);
  }, []);

  useEffect(() => {
    if (!siteKey || !el.current) return;
    let live = true;
    setBlocked(false);
    loadTurnstile().then((ts) => {
      if (!live || !el.current) return;
      widget.current = ts.render(el.current, {
        sitekey: siteKey, action, theme, size: 'flexible',
        callback: (t: string) => live && setToken(t),
        'expired-callback': () => live && setToken(null),
        'error-callback': () => { if (live) setToken(null); },
      });
    }).catch(() => live && setBlocked(true));
    return () => {
      live = false;
      if (widget.current) { try { window.turnstile?.remove(widget.current); } catch { /* already gone */ } }
      widget.current = null;
      setToken(null);
    };
  }, [siteKey, theme]);

  const reset = () => {
    setToken(null);
    if (widget.current) { try { window.turnstile?.reset(widget.current); } catch { /* widget removed */ } }
  };

  const box = siteKey ? (
    <div class="turnstile" data-testid="turnstile" data-state={token ? 'ready' : blocked ? 'blocked' : 'pending'}>
      <div ref={el} class="turnstile-slot" />
      {blocked && <p class="hint" role="alert" style="margin:0">Verification couldn’t load. Allow challenges.cloudflare.com or try another browser, then refresh.</p>}
    </div>
  ) : null;

  return {
    required: siteKey === undefined ? null : !!siteKey,
    token,
    box,
    reset,
    pendingMessage: blocked ? 'Verification couldn’t load, so this form can’t be sent. Refresh the page to try again.' : 'Please complete the verification check, then try again.',
  };
}
